import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { toggleSampleBasketMedia } from 'Apollo/Store/Showroom/UserView/Samples/Basket/resolvers';
import { setIsRequestSamplesFormOpen } from 'Apollo/Store/Showroom/UserView/Samples/resolvers';
import { MediaInfoFragment, useGetShowroomSamplesBasketQuery, useGetMobileMenuOpenedQuery, ShowroomMobileMenuType } from 'generated/graphql';

import { useGetCurrentShowroomData, useIsShowroomMobileVersion } from 'lib/showroom';

import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import MailOutlineIcon from '@material-ui/icons/MailOutline';

import { ReactComponent as RemoveFromSamplesBasketIcon } from 'icons/removeFromSamplesBasket.svg';

import ShowroomBasketLayout from '../BasketLayout';
import { BasketActionButton } from '../BasketLayout/ShowroomBasketLayout';
import ShowroomMobileActionsMenuLayout from '../MobileActionsMenuLayout';
import ShowroomSamplesForm from '../SamplesForm';
import ShowroomSamplesEmptyBasket from './EmptyBasket';
import { useStyles } from './style';

const ShowroomSamplesBasket = () => {
	const showroom = useGetCurrentShowroomData();
	const { id: showroomId, tenant } = showroom;
	const classes = useStyles();

	const { t } = useTranslation();
	const mobileVersion = useIsShowroomMobileVersion();
	const {
		data: { showroomSamplesBasket }
	} = useGetShowroomSamplesBasketQuery({ variables: { showroomId } });
	const {
		data: {
			showroom: {
				userView: { activeMediaMobileMenu }
			}
		}
	} = useGetMobileMenuOpenedQuery();

	const actions: BasketActionButton[] = React.useMemo(() => {
		const actions = [];
		if (showroomSamplesBasket.length > 0)
			actions.push({
				id: 'basketRequestBtn',
				label: t('showroom.samples.list.requestAll'),
				icon: <MailOutlineIcon />,
				onClick: () => setIsRequestSamplesFormOpen(true)
			});
		return actions;
	}, [showroomSamplesBasket.length, t]);

	const getMediaButtons = React.useCallback(
		(media: MediaInfoFragment) => (
			<IconButton
				id={`deleteBasketItem_${media.id}`}
				className={classes.deleteButton}
				onClick={() => toggleSampleBasketMedia(tenant.uuid, showroomId, media.id)}
				disableRipple
			>
				<DeleteOutlineIcon className={classes.deleteButtonIcon} />
			</IconButton>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const getMobileActions = React.useCallback(
		(media: MediaInfoFragment, closeMobileMenu: () => void) => (
			<List disablePadding>
				<ListItem
					id={`deleteBasketItemMobile_${media.id}`}
					className={classes.menuListItem}
					button
					onClick={() => {
						toggleSampleBasketMedia(tenant.uuid, showroomId, media.id);
						closeMobileMenu();
					}}
					disableRipple
				>
					<ListItemIcon>
						<RemoveFromSamplesBasketIcon />
					</ListItemIcon>
					<ListItemText primary={t('showroom.samples.list.remove')} classes={{ primary: classes.listItemText }} />
				</ListItem>
			</List>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[showroomId, tenant]
	);

	return (
		<div className={clsx({ [classes.samplesBasketWrapperMobile]: mobileVersion && showroomSamplesBasket.length > 0 })}>
			<ShowroomBasketLayout
				id={'showroomBasket'}
				title={t('showroom.samples.list.title')}
				medias={showroomSamplesBasket}
				actions={actions}
				elementName={t(`showroom.samples.list.${showroomSamplesBasket.length > 1 ? 'counterPlural' : 'counter'}`)}
				menuType={ShowroomMobileMenuType.SamplesBasket}
				getItemDesktopActions={getMediaButtons}
				getItemMobileActions={getMobileActions}
				emptyBasketComponent={<ShowroomSamplesEmptyBasket id={showroom.id}/>}
			/>
			{mobileVersion && showroomSamplesBasket.length > 0 && !activeMediaMobileMenu ? (
				<ShowroomMobileActionsMenuLayout open>
					<List disablePadding>
						<ListItem
							id="showroomMobileRequestSamplesOption"
							className={clsx(classes.menuListItem, classes.requestAllSamplesListItem)}
							button
							disableRipple
							onClick={() => setIsRequestSamplesFormOpen(true)}
						>
							<ListItemIcon className={classes.requestAllSamplesListItemIcon}>
								<MailOutlineIcon className={classes.requestAllSamplesIcon} />
							</ListItemIcon>
							<ListItemText
								primary={t('showroom.samples.list.requestAll')}
								classes={{
									root: classes.requestAllSamplesListItemRoot
								}}
							/>
						</ListItem>
					</List>
				</ShowroomMobileActionsMenuLayout>
			) : null}
			<ShowroomSamplesForm />
		</div>
	);
};

export default React.memo(ShowroomSamplesBasket);
